import { UserProfile } from '@starx/shared-types';

export function renderComplianceModal(
  profile: UserProfile,
  controls: any = {},
  kycDocs: any[] = [],
  activeTab: 'kyc' | 'controls' = 'kyc'
): string {
  const dailyLimit = controls.daily_loss_limit_cents != null ? (Number(controls.daily_loss_limit_cents) / 100).toFixed(2) : '';
  const coolingStatus = controls.cooling_off_until ? `Active until ${new Date(controls.cooling_off_until).toLocaleString()}` : 'Not active';
  const exclusionStatus = controls.self_excluded_until ? `Active until ${new Date(controls.self_excluded_until).toLocaleString()}` : 'Not active';
  const badgeTier = (profile.badge_tier || 'GREY').toUpperCase();
  const badgeColor = profile.badge_color || (badgeTier === 'VIP' ? '#c084fc' : badgeTier === 'GOLD' ? '#fbbf24' : badgeTier === 'GREEN' ? '#34d399' : badgeTier === 'BLUE' ? '#38bdf8' : '#94a3b8');
  const badgeName = profile.badge_name || (badgeTier === 'VIP' ? '👑 VIP Tier Investor' : badgeTier === 'GOLD' ? '🏆 Gold Pro Trader' : badgeTier === 'GREEN' ? '🟢 Verified Depositor' : badgeTier === 'BLUE' ? '🔵 KYC Verified' : '⚪ Non-Verified');
  const alias = profile.public_alias || profile.full_name || 'Trader';

  const countryCode = (profile.country_code || '').trim().toUpperCase();
  const flag = /^[A-Z]{2}$/.test(countryCode)
    ? String.fromCodePoint(...[...countryCode].map((c) => 127397 + c.charCodeAt(0)))
    : '🌐';
  const countryName = typeof Intl.DisplayNames === 'function' && /^[A-Z]{2}$/.test(countryCode)
    ? (new Intl.DisplayNames(['en'], { type: 'region' })).of(countryCode) || countryCode
    : countryCode;
  const countryDisplay = countryCode ? `${flag} ${countryName}` : '—';

  const kycStatus = (profile.kyc_status || 'UNVERIFIED').toUpperCase();
  const isKycVerified = kycStatus === 'VERIFIED' || (profile.kyc_tier && profile.kyc_tier >= 2);

  const getDocStatusBadge = (status: string) => {
    const s = (status || '').toUpperCase();
    if (s === 'APPROVED') {
      return '<span style="display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; background: rgba(16, 185, 129, 0.15); color: #10b981; border: 1px solid rgba(16, 185, 129, 0.3); padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; line-height: 1;">✓ APPROVED</span>';
    }
    if (s === 'REVIEW_PROPOSED' || s === 'IN_REVIEW') {
      return '<span style="display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.3); padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; line-height: 1;">⚙️ IN REVIEW</span>';
    }
    if (s === 'REJECTED') {
      return '<span style="display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; background: rgba(244, 63, 94, 0.15); color: #f43f5e; border: 1px solid rgba(244, 63, 94, 0.3); padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; line-height: 1;">✕ REJECTED</span>';
    }
    return '<span style="display: inline-flex; align-items: center; gap: 4px; white-space: nowrap; background: rgba(245, 158, 11, 0.15); color: #fbbf24; border: 1px solid rgba(245, 158, 11, 0.3); padding: 4px 10px; border-radius: 6px; font-size: 11px; font-weight: 700; line-height: 1;">⏳ PENDING REVIEW</span>';
  };

  const escape = (str: string) => (str || '').replace(/[&<>"']/g, (m) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m] || m));

  const isIndian = countryCode === 'IN';

  const docsHtml = kycDocs.length > 0 ? `
    <div style="margin-top: 12px; border: 1px solid #1e293b; border-radius: 8px; overflow-x: auto;">
      <table style="width: 100%; min-width: 500px; border-collapse: collapse; font-size: 12px; text-align: left;">
        <thead>
          <tr style="background: #0f172a; border-bottom: 1px solid #1e293b; color: #94a3b8;">
            <th style="padding: 10px 12px; white-space: nowrap;">Type & ID</th>
            <th style="padding: 10px 12px; white-space: nowrap;">Reference</th>
            <th style="padding: 10px 12px; white-space: nowrap; min-width: 150px;">Status</th>
            <th style="padding: 10px 12px; white-space: nowrap;">Submitted</th>
          </tr>
        </thead>
        <tbody>
          ${kycDocs.map(d => `
            <tr style="border-bottom: 1px solid #172033;">
              <td style="padding: 10px 12px; font-weight: 600; color: #f8fafc; white-space: nowrap;">
                ${escape(d.document_type || 'DOCUMENT')}
                ${d.id_number ? `<div style="color: #38bdf8; font-size: 10px; font-family: monospace; font-weight: normal; margin-top: 2px;"># ${escape(d.id_number)}</div>` : ''}
              </td>
              <td style="padding: 10px 12px; color: #64748b; font-family: monospace; white-space: nowrap;">
                ${escape((d.object_key || '').split('/').pop() || d.document_id || 'doc-ref')}
              </td>
              <td style="padding: 10px 12px; white-space: nowrap; min-width: 150px;">
                ${getDocStatusBadge(d.status)}
              </td>
              <td style="padding: 10px 12px; color: #94a3b8; font-size: 11px; white-space: nowrap;">
                ${d.submitted_at ? new Date(d.submitted_at).toLocaleDateString() : '—'}
              </td>
            </tr>
          `).join('')}
        </tbody>
      </table>
    </div>
  ` : `
    <div style="background: #0b0e14; border: 1px dashed #273549; border-radius: 8px; padding: 16px; text-align: center; color: #64748b; font-size: 12px; margin-top: 10px;">
      No identity documents uploaded yet. Upload an official government ID or passport below to start Tier 2 verification.
    </div>
  `;

  return `
    <div class="compliance-modal" style="background: #121824; border: 1px solid #1e293b; border-radius: 14px; padding: 24px; max-width: 640px; width: 100%; max-height: 88vh; overflow-y: auto; color: #f8fafc; font-family: 'Inter', -apple-system, sans-serif; box-shadow: 0 25px 50px -12px rgba(0,0,0,0.6); box-sizing: border-box;">
      
      <!-- Modal Header -->
      <div style="display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 1px solid #1e293b; padding-bottom: 14px; margin-bottom: 16px; position: sticky; top: -24px; background: #121824; z-index: 10; margin-top: -8px; padding-top: 8px;">
        <div>
          <h3 style="margin: 0; font-size: 18px; font-weight: 700; color: #ffffff; display: flex; align-items: center; gap: 8px;">
            <span>🛡️</span> Compliance & KYC Verification
          </h3>
          <span style="font-size: 12px; color: #94a3b8;">Tiered identity verification, institutional compliance & account safeguards</span>
        </div>
        <button id="close-compliance-btn" style="background: transparent; border: none; color: #94a3b8; font-size: 20px; cursor: pointer; line-height: 1; padding: 4px; border-radius: 4px;" title="Close">✕</button>
      </div>

      <!-- Navigation Tabs -->
      <div style="display: flex; gap: 8px; border-bottom: 1px solid #1e293b; padding-bottom: 12px; margin-bottom: 16px;">
        <button id="tab-btn-kyc" class="compliance-tab-btn" style="flex: 1; background: ${activeTab === 'kyc' ? '#1e293b' : 'transparent'}; color: ${activeTab === 'kyc' ? '#38bdf8' : '#94a3b8'}; border: 1px solid ${activeTab === 'kyc' ? '#0284c7' : '#334155'}; padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px;">
          🪪 Identity Verification (KYC)
        </button>
        <button id="tab-btn-controls" class="compliance-tab-btn" style="flex: 1; background: ${activeTab === 'controls' ? '#1e293b' : 'transparent'}; color: ${activeTab === 'controls' ? '#38bdf8' : '#94a3b8'}; border: 1px solid ${activeTab === 'controls' ? '#0284c7' : '#334155'}; padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px;">
          🛡️ Responsible Limits
        </button>
      </div>

      <!-- TAB 1: KYC VERIFICATION -->
      <div id="compliance-tab-kyc" style="display: ${activeTab === 'kyc' ? 'block' : 'none'};">
        
        <!-- Summary Stats Card -->
        <div style="background: #0b0e14; border: 1px solid #1e293b; padding: 14px 16px; border-radius: 10px; margin-bottom: 16px; font-size: 13px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 9px;">
            <span style="color: #64748b;">Trader Profile</span>
            <strong style="color: #f8fafc;">${escape(alias)}</strong>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 9px;">
            <span style="color: #64748b;">Verified Badge Tier</span>
            <strong style="color: ${badgeColor}; font-weight: 700;">${badgeName}</strong>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 9px; gap: 12px;">
            <span style="color: #64748b; white-space: nowrap;">KYC Verification Status</span>
            <div style="display: flex; align-items: center; gap: 8px; flex-shrink: 0;">
              <span style="color: ${isKycVerified ? '#10b981' : '#fbbf24'}; font-weight: 700; white-space: nowrap;">Tier ${profile.kyc_tier || 1}</span>
              ${getDocStatusBadge(kycStatus)}
            </div>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="color: #64748b;">Country</span>
            <strong style="color: #38bdf8;">${countryDisplay}</strong>
          </div>
        </div>

        <!-- Tier Progression Guide -->
        <div style="background: rgba(30, 41, 59, 0.4); border: 1px solid #1e293b; border-radius: 8px; padding: 12px; margin-bottom: 16px; font-size: 11px; line-height: 1.5; color: #94a3b8;">
          <strong style="color: #f8fafc; display: block; margin-bottom: 4px;">KYC Tier Benefits:</strong>
          • <strong>Tier 1 (Non-verified):</strong> Limited withdrawals, grey profile badge.<br>
          • <strong>Tier 2 (Verified):</strong> Submit mandatory identity document (${isIndian ? 'Aadhaar, Passport, DL, or National ID' : 'National ID, Passport, or Driving License'}). Unlocks unlimited withdrawals, priority order matching & 🔵 Blue Verified badge.<br>
          • <strong>Tier 3 (Depositor & VIP):</strong> Real deposits & active trading unlock 🟢 Depositor & 👑 VIP badges with custom bonuses.
        </div>

        <!-- Submitted Documents History -->
        <div style="margin-bottom: 18px;">
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <h4 style="margin: 0; font-size: 13px; font-weight: 700; color: #e2e8f0; text-transform: uppercase; letter-spacing: 0.5px;">Submitted Evidence</h4>
            <span style="font-size: 11px; color: #64748b;">${kycDocs.length} document(s)</span>
          </div>
          ${docsHtml}
        </div>

        <!-- Document Upload Section -->
        <div style="background: #0d121c; border: 1px solid #1e293b; border-radius: 10px; padding: 16px; margin-bottom: 16px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px;">
            <h4 style="margin: 0; font-size: 13px; font-weight: 700; color: #f8fafc;">Submit Identity Verification Document</h4>
            ${isIndian ? '<span style="font-size: 10px; background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.3); border-radius: 4px; padding: 2px 6px; font-weight: 700;">🇮🇳 INDIA KYC</span>' : '<span style="font-size: 10px; background: rgba(148, 163, 184, 0.15); color: #94a3b8; border: 1px solid rgba(148, 163, 184, 0.3); border-radius: 4px; padding: 2px 6px; font-weight: 700;">🌐 GLOBAL KYC</span>'}
          </div>
          <p style="margin: 0 0 12px; font-size: 12px; color: #94a3b8;">
            ${isIndian
              ? 'Indian regulations require submitting any 1 mandatory document: Aadhaar Card, Passport, Driving License, or National ID (PAN / Voter ID).'
              : 'Submit any 1 mandatory government-issued document: National ID Card, Passport, or Driver\'s License.'
            }
          </p>
          
          <form id="trader-kyc-upload-form" style="display: grid; gap: 10px;">
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px;">
              <div>
                <label style="font-size: 11px; color: #94a3b8; display: block; margin-bottom: 4px;">Document Type <strong style="color: #f43f5e;">*</strong></label>
                <select id="kyc-doc-type" required style="width: 100%; background: #161f30; border: 1px solid #334155; color: #f8fafc; padding: 8px 10px; border-radius: 6px; font-size: 13px;">
                  ${isIndian ? `
                    <option value="AADHAAR">🇮🇳 Aadhaar Card (UIDAI 12-Digit)</option>
                    <option value="PASSPORT">Passport</option>
                    <option value="DRIVING_LICENSE">Driving License</option>
                    <option value="NATIONAL_ID">National ID / PAN / Voter ID</option>
                  ` : `
                    <option value="NATIONAL_ID">National ID Card</option>
                    <option value="PASSPORT">Passport</option>
                    <option value="DRIVING_LICENSE">Driver's License</option>
                  `}
                </select>
              </div>
              <div>
                <label style="font-size: 11px; color: #94a3b8; display: block; margin-bottom: 4px;">Document Format</label>
                <select id="kyc-content-type" style="width: 100%; background: #161f30; border: 1px solid #334155; color: #f8fafc; padding: 8px 10px; border-radius: 6px; font-size: 13px;">
                  <option value="application/pdf">PDF Document (.pdf)</option>
                  <option value="image/jpeg">JPEG Image (.jpg)</option>
                  <option value="image/png">PNG Image (.png)</option>
                </select>
              </div>
            </div>

            <div>
              <label style="font-size: 11px; color: #94a3b8; display: flex; justify-content: space-between; margin-bottom: 4px;">
                <span>Document ID / Serial Number <strong style="color: #f43f5e;">*</strong></span>
                <span id="kyc-id-format-hint" style="color: #38bdf8; font-size: 10px;">${isIndian ? '12-digit Aadhaar' : 'Alpha-numeric Serial'}</span>
              </label>
              <input type="text" id="kyc-id-number" required placeholder="${isIndian ? 'Enter 12-digit Aadhaar number (e.g. 5432 9876 1234)' : 'Enter official document / passport number'}" style="width: 100%; background: #161f30; border: 1px solid #334155; color: #f8fafc; padding: 8px 10px; border-radius: 6px; font-size: 13px; box-sizing: border-box; font-family: monospace;" autocomplete="off" />
            </div>

            <div>
              <label style="font-size: 11px; color: #94a3b8; display: block; margin-bottom: 4px;">Select Document File</label>
              <input type="file" id="kyc-file-input" accept=".pdf,.png,.jpg,.jpeg" style="width: 100%; background: #161f30; border: 1px solid #334155; color: #94a3b8; padding: 6px 10px; border-radius: 6px; font-size: 12px; box-sizing: border-box;" />
            </div>

            <button type="submit" id="submit-kyc-doc-btn" style="background: #0284c7; color: #ffffff; border: none; padding: 10px 16px; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 8px; margin-top: 4px;">
              <span>📤</span> Upload & Submit for Verification
            </button>
            <div id="kyc-submit-status" aria-live="polite" style="font-size: 12px; min-height: 18px; margin-top: 2px;"></div>
          </form>
        </div>

      </div>

      <!-- TAB 2: RESPONSIBLE TRADING CONTROLS -->
      <div id="compliance-tab-controls" style="display: ${activeTab === 'controls' ? 'block' : 'none'};">
        
        <div style="background: #0b0e14; padding: 14px 16px; border-radius: 8px; margin-bottom: 16px; font-size: 13px; border: 1px solid #1e293b;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <span style="color: #64748b;">Account Restriction Status</span>
            <strong style="color: ${profile.is_restricted ? '#f43f5e' : '#10b981'};">
              ${profile.is_restricted ? 'RESTRICTED' : 'NORMAL (UNRESTRICTED)'}
            </strong>
          </div>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span style="color: #64748b;">Active Protection Limits</span>
            <strong style="color: #38bdf8;">${dailyLimit ? `$${dailyLimit}/day` : 'None configured'}</strong>
          </div>
        </div>

        <div style="margin-bottom: 16px;">
          <label style="font-size: 12px; color: #94a3b8; display: block; margin-bottom: 6px;">Daily Loss Limit (USD)</label>
          <div style="display: flex; gap: 8px;">
            <input type="number" id="daily-loss-limit" min="1" step="0.01" value="${dailyLimit}" placeholder="e.g. 500.00" style="background: #0b0e14; border: 1px solid #334155; color: #ffffff; padding: 8px 12px; border-radius: 6px; width: 100%; box-sizing: border-box; font-size: 14px;" />
            <button id="save-daily-loss-limit-btn" style="background: #075985; color: #e0f2fe; border: 1px solid #0284c7; border-radius: 6px; padding: 8px 14px; white-space: nowrap; cursor: pointer; font-weight: 600; font-size: 13px;">Save limit</button>
          </div>
        </div>

        <div style="display: flex; gap: 10px; margin-bottom: 16px;">
          <button id="cooling-off-btn" style="flex: 1; background: #1e293b; color: #f8fafc; border: 1px solid #334155; padding: 10px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer;">
            ⏳ 24h Cooling-Off
          </button>
          <button id="self-exclude-btn" style="flex: 1; background: #7f1d1d; color: #fecaca; border: 1px solid #991b1b; padding: 10px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer;">
            🛑 Self-Exclusion
          </button>
        </div>
        <div id="responsible-control-status" aria-live="polite" style="font-size: 11px; color: #94a3b8; margin: -6px 0 16px; line-height: 1.5;">Cooling-off: ${coolingStatus}<br>Self-exclusion: ${exclusionStatus}</div>

        <div style="background: #1e293b; padding: 10px 14px; border-radius: 6px; font-size: 11px; color: #94a3b8; margin-bottom: 16px; line-height: 1.4;">
          ⚠️ Fixed-time options involve significant risk of capital loss. Do not invest funds you cannot afford to lose. All quotes are pre-locked via authoritative cryptographic signatures.
        </div>
      </div>

      <!-- Bottom Action Bar -->
      <div style="display: flex; justify-content: flex-end; border-top: 1px solid #1e293b; padding-top: 14px;">
        <button id="dismiss-compliance-btn" style="background: #38bdf8; color: #020617; border: none; padding: 8px 20px; border-radius: 6px; font-size: 13px; cursor: pointer; font-weight: 700;">
          Close
        </button>
      </div>

    </div>
  `;
}
